import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Layout from '../../components/Layout';
import Card from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import { aiService } from '../../services/ai.service';
import { LlmUsageLog } from '../../interfaces/ai.interface';
import { getErrorMsg } from '../../utils/error';

const PAGE_SIZE = 20;

const AiLogsPage = () => {
  const { t } = useTranslation('common');

  const [rows, setRows] = useState<LlmUsageLog[]>([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [formTypeFilter, setFormTypeFilter] = useState('');
  const [modelFilter, setModelFilter] = useState('');

  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const load = useCallback(
    async (p: number, formType: string, model: string) => {
      setLoading(true);
      setError('');
      try {
        const params: { page: number; limit: number; formType?: string; model?: string } = {
          page: p,
          limit: PAGE_SIZE,
        };
        if (formType.trim()) params.formType = formType.trim();
        if (model.trim()) params.model = model.trim();

        const res = await aiService.getLogs(params);
        setRows(res.data);
        setPage(res.meta.page);
        setTotalPages(res.meta.totalPages);
        setTotal(res.meta.total);
      } catch (e) {
        setError(getErrorMsg(e, t('failed_to_load')));
      } finally {
        setLoading(false);
      }
    },
    [t],
  );

  useEffect(() => {
    load(1, formTypeFilter, modelFilter);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  const handleFilterChange = (newFormType: string, newModel: string) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      load(1, newFormType, newModel);
    }, 400);
  };

  const handleFormTypeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setFormTypeFilter(val);
    handleFilterChange(val, modelFilter);
  };

  const handleModelChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setModelFilter(val);
    handleFilterChange(formTypeFilter, val);
  };

  const handlePageChange = (newPage: number) => {
    load(newPage, formTypeFilter, modelFilter);
  };

  const thClass =
    'px-4 py-2 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider';
  const tdClass = 'px-4 py-3 text-sm text-gray-800 dark:text-gray-200';

  return (
    <Layout title={t('ai_logs')}>
      <div className="space-y-4">
        {/* Filter bar */}
        <Card className="p-4">
          <div className="flex flex-wrap gap-3">
            <input
              type="text"
              value={formTypeFilter}
              onChange={handleFormTypeChange}
              placeholder={t('filter_by_form_type')}
              aria-label={t('filter_by_form_type')}
              className="rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 min-w-[200px]"
            />
            <input
              type="text"
              value={modelFilter}
              onChange={handleModelChange}
              placeholder={t('filter_by_model')}
              aria-label={t('filter_by_model')}
              className="rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 min-w-[200px]"
            />
          </div>
        </Card>

        {/* Error banner */}
        {error && (
          <div className="p-3 rounded-md bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 text-sm border border-red-100 dark:border-red-800">
            {error}
          </div>
        )}

        {/* Table */}
        <Card className="overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
                <tr>
                  <th scope="col" className={thClass}>{t('date_time')}</th>
                  <th scope="col" className={thClass}>{t('user')}</th>
                  <th scope="col" className={thClass}>{t('model')}</th>
                  <th scope="col" className={thClass}>{t('form_type')}</th>
                  <th scope="col" className={thClass}>{t('target_field')}</th>
                  <th scope="col" className={thClass}>{t('input_tokens')}</th>
                  <th scope="col" className={thClass}>{t('output_tokens')}</th>
                  <th scope="col" className={thClass}>{t('duration_ms')}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                {loading ? (
                  <tr>
                    <td colSpan={8} className="px-4 py-8 text-center text-sm text-gray-400 dark:text-gray-500">
                      {t('loading')}
                    </td>
                  </tr>
                ) : rows.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="px-4 py-8 text-center text-sm text-gray-400 dark:text-gray-500">
                      {t('no_records')}
                    </td>
                  </tr>
                ) : (
                  rows.map((log) => (
                    <tr key={log.id} className="hover:bg-gray-50 dark:hover:bg-gray-800/50">
                      <td className={tdClass}>
                        {new Date(log.createdAt).toLocaleString()}
                      </td>
                      <td className={tdClass}>
                        <span className="font-mono text-xs truncate max-w-[120px] block">
                          {log.userId ?? '—'}
                        </span>
                      </td>
                      <td className={tdClass}>{log.model}</td>
                      <td className={tdClass}>{log.formType ?? '—'}</td>
                      <td className={tdClass}>{log.fieldKey ?? '—'}</td>
                      <td className={tdClass}>{log.inputTokens ?? '—'}</td>
                      <td className={tdClass}>{log.outputTokens ?? '—'}</td>
                      <td className={tdClass}>
                        {log.durationMs != null ? `${log.durationMs}ms` : '—'}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {!loading && (
            <div className="px-4 py-3 flex items-center justify-between border-t border-gray-100 dark:border-gray-700">
              <span className="text-xs text-gray-500 dark:text-gray-400">
                {total} {t('total_records')}
              </span>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(page - 1)}
                  disabled={page <= 1}
                >
                  {t('previous')}
                </Button>
                <span className="text-sm text-gray-600 dark:text-gray-400 px-2">
                  {page} {t('of')} {totalPages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(page + 1)}
                  disabled={page >= totalPages}
                >
                  {t('next')}
                </Button>
              </div>
            </div>
          )}
        </Card>
      </div>
    </Layout>
  );
};

export default AiLogsPage;
